/**
 * @ngdoc service
 * @name Storage
 * @description
 * Provides a key-value store using localStorage to persist data.
 */
import { Injectable } from '@angular/core';
import { StorageModel } from 'app/models';


@Injectable()
export class StorageService {

    /**
     * @ngdoc method
     * @description
     * Retrieve a value from the storage by key,
     * optionally supplying a placeholder if missing.
     *
     * @param key Used to retrieve the value with
     * @param placeholder Optional value to use if the key is not found
     * @returns value found in storage, or placeholder if not found
     * @throws if the key is not found, and a placeholder is not given
     */
    get<T>(key: string, placeholder?: T): T {
        if (localStorage[key] !== undefined) {

            try {
                const datum: StorageModel = JSON.parse(localStorage[key]);

                if (!datum.expiry || datum.expiry > ((new Date()).getTime() / 1000 || 0)) {
                    return datum.value;
                }
            } catch (e) {
                this.delete(key);
            }
        }

        return placeholder;
    }

    /**
     * @ngdoc method
     * @description
     * Store a value storage, optionally supplying an expiry time.
     *
     * @param key to store the value under
     * @param value to store
     * @param expiry number of seconds before the value should expire (default is never)
     * @returns value passed in
     * @throws if the value is a function
     */
    set<T>(key: string, value: T, expiry?: number): T {
        const datum: StorageModel = new StorageModel(value);

        if (expiry) {
            datum.expiry = ((new Date()).getTime() / 1000 || 0) + expiry;
        }

        try {
            this.delete(key);
            localStorage[key] = JSON.stringify(datum);
        } catch (e) {
            console.error('Set storage', e);
        }

        return value;
    }

    /**
     * @ngdoc method
     * @description
     * Check if a value is in the storage
     *
     * @param key to check
     * @returns true if the key exists in the storage, false otherwise
     */
    has<T>(key: string): boolean {
        return this.get<T>(key) !== undefined;
    }

    /**
     * @ngdoc method
     * @description
     * Deletes a value from the storage
     *
     * @param key to delete
     */
    delete(key: string): void {
        delete localStorage[key];
    }

    /**
     * @ngdoc method
     * @description
     * Clears all keys set in the storage
     */
    clear(): void {
        Object.keys(localStorage).forEach(key => this.delete(key));
    }
}
